// Service for validating exported Word documents
import JSZip from 'jszip';

/**
 * Validates that an exported Word document blob is properly formatted
 * @param {Blob} blob - The exported Word document blob
 * @returns {Promise<Object>} - Validation result with details
 */
export async function validateWordExport(blob) {
    const validation = {
        isValid: false,
        fileSize: blob.size,
        errors: [],
        warnings: [],
        details: {}
    };

    try {
        // Check blob size
        if (blob.size === 0) {
            validation.errors.push('Exported file is empty (0 bytes)');
            return validation;
        }

        if (blob.size < 1000) {
            validation.warnings.push(`File size is very small (${blob.size} bytes), may indicate export issues`);
        }

        // Check MIME type
        if (blob.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            validation.warnings.push(`Unexpected MIME type: ${blob.type}`);
        }

        // Convert blob to array buffer for ZIP validation
        const arrayBuffer = await blob.arrayBuffer();
        validation.details.arrayBufferSize = arrayBuffer.byteLength;

        // Check if it starts with ZIP signature
        const firstBytes = new Uint8Array(arrayBuffer.slice(0, 4));
        const hasZipSignature = firstBytes[0] === 0x50 && firstBytes[1] === 0x4B;
        
        if (!hasZipSignature) {
            validation.errors.push('File does not have valid ZIP signature (Word documents are ZIP files)');
            return validation;
        }

        // Try to load as ZIP
        let zip;
        try {
            zip = await JSZip.loadAsync(arrayBuffer);
            validation.details.zipFilesCount = Object.keys(zip.files).length;
        } catch (error) {
            validation.errors.push(`Failed to parse as ZIP file: ${error.message}`);
            return validation;
        }

        // Check for required Word document files
        const requiredFiles = [
            '[Content_Types].xml',
            '_rels/.rels',
            'word/document.xml',
            'word/_rels/document.xml.rels'
        ];

        const presentFiles = Object.keys(zip.files);
        validation.details.presentFiles = presentFiles;

        for (const requiredFile of requiredFiles) {
            if (!zip.files[requiredFile]) {
                validation.errors.push(`Missing required Word document file: ${requiredFile}`);
            }
        }

        // Check document.xml content
        if (zip.files['word/document.xml']) {
            try {
                const documentXml = await zip.files['word/document.xml'].async('string');
                validation.details.documentXmlLength = documentXml.length;
                
                if (documentXml.length === 0) {
                    validation.errors.push('document.xml is empty');
                } else if (documentXml.length < 100) {
                    validation.warnings.push('document.xml is very short, may not contain actual content');
                }

                // Check for basic Word document structure
                if (!documentXml.includes('<w:document')) {
                    validation.errors.push('document.xml does not contain valid Word document structure');
                }
            } catch (error) {
                validation.errors.push(`Failed to read document.xml: ${error.message}`);
            }
        }

        // If no errors, mark as valid
        validation.isValid = validation.errors.length === 0;

        return validation;

    } catch (error) {
        validation.errors.push(`Unexpected validation error: ${error.message}`);
        return validation;
    }
}

/**
 * Creates a detailed validation report string
 * @param {Object} validation - Validation result from validateWordExport
 * @returns {string} - Human-readable validation report
 */
export function createValidationReport(validation) {
    let report = `Word Document Export Validation Report\n`;
    report += `=========================================\n\n`;
    
    report += `File Size: ${validation.fileSize} bytes\n`;
    report += `Valid: ${validation.isValid ? 'YES' : 'NO'}\n\n`;

    if (validation.errors.length > 0) {
        report += `ERRORS:\n`;
        validation.errors.forEach((error, index) => {
            report += `  ${index + 1}. ${error}\n`;
        });
        report += `\n`;
    }

    if (validation.warnings.length > 0) {
        report += `WARNINGS:\n`;
        validation.warnings.forEach((warning, index) => {
            report += `  ${index + 1}. ${warning}\n`;
        });
        report += `\n`;
    }

    if (validation.details && Object.keys(validation.details).length > 0) {
        report += `DETAILS:\n`;
        Object.entries(validation.details).forEach(([key, value]) => {
            if (key === 'presentFiles' && Array.isArray(value)) {
                report += `  ${key}: ${value.length} files\n`;
                if (value.length <= 20) {
                    value.forEach(file => report += `    - ${file}\n`);
                } else {
                    report += `    (Too many files to list)\n`;
                }
            } else {
                report += `  ${key}: ${value}\n`;
            }
        });
    }

    return report;
}

export default { validateWordExport, createValidationReport };